import { cloneElement } from 'react';

import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

import { ensureDefined } from '@e-socna/utils';
import type { GenericStatusId } from '@e-socna/data-layer';

import { useReferencesContext } from '../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { colorToMuiColor } from '../utils/color-to-mui-color.util';
import { iconToMuiIcon } from '../utils/icon-to-mui-icon.util';

interface Props {
  readonly statusId: GenericStatusId;
  readonly variant?: 'chip' | 'icon';
}

export const GenericStatusIndicator = ({
  statusId,
  variant = 'chip',
}: Readonly<Props>) => {
  const { genericStatusesIdMap } = useReferencesContext();

  const { genericStatus } = ensureDefined({
    genericStatus: genericStatusesIdMap[statusId],
  });

  switch (variant) {
    case 'chip': {
      return (
        <Chip
          color={colorToMuiColor(genericStatus.chip.color)}
          icon={iconToMuiIcon(genericStatus.chip.icon)}
          label={genericStatus.label}
          variant="filled"
        />
      );
    }
    case 'icon': {
      const muiColor = colorToMuiColor(genericStatus.chip.color);

      return (
        <Tooltip title={genericStatus.label}>
          {cloneElement(iconToMuiIcon(genericStatus.chip.icon), {
            // SvgIcon has no 'default' color, unlike Chip: fall back to 'inherit'.
            color: muiColor === 'default' ? 'inherit' : muiColor,
          })}
        </Tooltip>
      );
    }
    default: {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Unknown GenericStatusIndicator variant: ${variant}`);
    }
  }
};
